<template>
  <div class="cart-widget__item-internet">
    <div>
      <LazyBackgroundMedia
          v-if="hasCategoryImage"
          class="cart-widget__item-internet__icon"
          icon
          :media-url="product.category.image?.url"
          background-position-y="center"
          background-position-x="center"
          no-repeat
      />
    </div>
    <div>
      <div class="cart-widget__item-internet__title">
        <div>{{ product.name }}</div>
        <div>
          <button
              class="btn-remove-item"
              @click="$.emit('remove')"
          >
            <FontAwesomeIcon :icon="faTrashAlt" />
          </button>
        </div>
      </div>
      <div class="cart-widget__item-internet__price">
        <div style="grid-column: 1  / 2; grid-row: 1">einmalig</div>
        <div style="grid-column: 2  / 3; grid-row: 1">{{ product.getOneTimePriceValueFormatted(['setup_fee', 'base_price'], 2, true) }}&thinsp;€</div>
        <div style="grid-column: 1  / 2; grid-row: 2">pro Monat</div>
        <div style="grid-column: 2  / 3; grid-row: 2">{{ product.getRecurringPriceWithSavingsFormatted('base_price', 2, true) }}&thinsp;€</div>
        <div
            v-if="followUpPriceString"
            class="follow-up"
            style="grid-column: 1  / 3; grid-row: 3"
            v-html="followUpPriceString"
        />
      </div>
      <div class="card-widget__item__details">
        <Expand title="Produktdetails">
          <PriceDetailsTableInternet
              :product="product"
              :key="`details-${itemKey}`"
              with-addons
          />
        </Expand>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Product from '@/classes/Product';
import Expand from '@/components/Expand.vue';
import PriceDetailsTableInternet from '@/components/product/PriceDetailsTableInternet.vue';
import { formatNumber } from '@/helpers';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { computed } from 'vue';

library.add(faTrashAlt);

const props = defineProps<{
  itemKey: string
  product: Product
}>();

const hasCategoryImage = computed(() => !!props.product.category.image?.url);

const followUpPriceString = computed<string | undefined>(() => {
  const savings = props.product.getPriceSavings();

  if (savings.length === 0) return undefined;

  const savingStrings: string[] = [];

  savings.forEach((saving, inx) => {
    let savingPrice: string | number = props.product.getRecurringPriceValue('base_price', true) - saving.value;
    if (savingPrice < 0) savingPrice = 0;
    savingPrice = `${formatNumber(savingPrice, 2)}&thinsp;€&thinsp;/&thinsp;Monat`;

    if (inx === 0) {
      savingStrings.push(`für ${saving.toMonth > 1 ? `${saving.toMonth} Monate` : 'einen Monat'}`);
    } else {
      const savingDuration = (saving.toMonth - (saving.fromMonth - 1)) || 1;
      savingStrings.push(`danach für ${savingDuration > 1 ? `weitere ${savingDuration} Monate` : 'einen weiteren Monat'} ${savingPrice}`);
    }
  });

  savingStrings.push(`danach ${props.product.getRecurringPriceValueFormatted('base_price', 2, true)}&thinsp;€&thinsp;/&thinsp;Monat`);

  return savingStrings.join(', ');
});
</script>

<style lang="scss">
@import "src/scss/component-helpers";

.cart-widget__item-internet {
  display: grid;
  gap: 20px;
  grid-template-columns: auto 1fr;
  align-items: start;

  @media(min-width: map-get($grid-breakpoints, 'lg')) {
    gap: 30px;
  }

  > div:last-child {
    display: grid;
    grid-template-columns: 100%;
    gap: 10px;
  }

  &__icon {
    font-size: 60px;
    background-color: #fff;
    width: 1em;
    height: 1em;
    z-index: 3;

    .icon {
      background-size: contain;
    }

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      font-size: 90px;
    }
  }

  &__title, &__price {
    display: grid;
    grid-template-columns: auto auto;
    justify-content: space-between;
  }

  &__title {
    line-height: 1;

    > div {
      &:first-child {
        font-weight: 700;
        font-size: 1.125rem;

        @media (min-width: map_get($grid-breakpoints, 'lg')) {
          font-size: 1.5rem;
        }
      }
    }
  }

  &__price {
    > div {
      &:nth-child(2n + 2) {
        font-weight: 700;
        text-align: right;
      }

      &.follow-up {
        font-size: .75rem;
        text-align: right;
      }
    }
  }

  .btn-remove-item {
    font-size: 1.125rem;
    border: 0;
    background: transparent;
    padding: 0;
    outline: 0;
    color: #F93F4D;
    line-height: 0;

    @media (min-width: map_get($grid-breakpoints, 'lg')) {
      font-size: 1.75rem;
    }
  }
}
</style>
